'use client'

import {useId, useRef, type ReactNode} from 'react'
import {cn} from './cn'
import {Reveal} from './Reveal'

interface SectionAccordionProps {
    title: string
    count?: number
    summary?: ReactNode
    description?: ReactNode
    /** `row`: a borderless list row for details windows. */
    variant?: 'section' | 'filter' | 'pricing' | 'row'
    contentPadding?: 'normal' | 'none'
    isOpen: boolean
    onToggle: () => void
    children: ReactNode
}

/** Shared disclosure shell. Layout outside the section belongs to its parent. */
export function SectionAccordion({title, count, summary, description, variant = 'section',
    contentPadding = 'normal', isOpen, onToggle, children}: SectionAccordionProps) {
    const id = useId()
    const trigger = useRef<HTMLButtonElement>(null)
    const panel = useRef<HTMLDivElement>(null)
    const toggle = () => {
        if (isOpen && panel.current?.contains(document.activeElement)) trigger.current?.focus()
        onToggle()
    }
    return <div className={cn('min-w-0', variant === 'row' ? 'border-t border-gray-200 dark:border-gray-700'
        : variant !== 'pricing' && 'rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900',
        variant === 'section' ? 'shadow-sm' : variant !== 'row' && 'overflow-hidden')}>
        <button ref={trigger} id={`${id}-trigger`} type="button" onClick={toggle}
            aria-controls={`${id}-panel`} aria-expanded={isOpen}
            className={cn('flex min-h-12 w-full items-center justify-between gap-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500/40',
                variant === 'pricing' ? 'py-4' : variant === 'row' ? 'py-3' : variant === 'section' ? 'px-4 py-3' : 'px-3 py-2.5 transition-colors hover:bg-gray-50 dark:hover:bg-gray-800')}>
            <span className="min-w-0">
                <span className={cn("block font-semibold text-gray-900 dark:text-gray-100", variant === "pricing" ? "text-lg leading-7 sm:text-xl" : "text-sm leading-6")}>{title}</span>
                {description ? <span className="mt-1 block text-xs font-normal text-gray-500 dark:text-gray-400">{description}</span> : null}
            </span>
            <span className="flex shrink-0 items-center gap-3 text-xs text-gray-600 dark:text-gray-400">
                {summary ? <span className="max-w-[220px] truncate text-right">{summary}</span> : null}
                {typeof count === 'number' ? <span className="inline-flex h-6 min-w-8 items-center justify-center rounded-full bg-gray-100 px-2 leading-4 text-gray-800 dark:bg-gray-800 dark:text-gray-100">{count}</span> : null}
                <svg aria-hidden="true" className={cn('h-4 w-4 shrink-0 transition-transform duration-normal ease-out motion-reduce:transition-none', isOpen && 'rotate-180')}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
                </svg>
            </span>
        </button>
        <Reveal show={isOpen}>
            <div ref={panel} id={`${id}-panel`} role="region" aria-labelledby={`${id}-trigger`}
                className={cn(variant === 'pricing' ? 'pb-4' : variant === 'row' ? 'pb-3' : 'border-t border-gray-100 dark:border-gray-700', variant === 'section' && contentPadding === 'normal' && 'p-4')}>
                {children}
            </div>
        </Reveal>
    </div>
}
